import type { HTMLAttributes, KeyboardEvent, MouseEvent, ReactNode } from 'react';

import React, { useEffect, useRef, useState } from 'react';

import { SelectMenu } from './select-menu';
import { ChevronIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import { InputChip } from '../chip';

import styles from './select.module.css';

export interface SelectOption {
  [key: string]: any;
  hint?: string;
  disabled?: boolean;
}

export type SelectProps = {
  value?: SelectOption | SelectOption[];
  options?: SelectOption[];
  optionsIdProperty?: string;
  getOptionsLabel?: (option: SelectOption) => string;
  getDisplayLabel?: (option: SelectOption) => string;
  disabled?: boolean;
  isSearchable?: boolean;
  forceMenuWidth?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  itemsClassName?: string;
  iconComponent?: any;
  icon?: ReactNode;
  'data-testid'?: string;
  onChange?: (option: SelectOption | SelectOption[]) => void;
  onMenuToggle?: (isOpen: boolean) => void;
} & Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>;

const MOBILE_BREAKPOINT = '(max-width: 768px)';

/**
 * @param {SelectOption | SelectOption[]} [value] - The value of the selected option(s).
 * @default ''
 * @param {SelectOption[]} [options] - An array of options to be displayed in the dropdown.
 * @default []
 * @param {string} [optionsIdProperty] - Specify the property to be used as the id of the options objects.
 * @default 'id'
 * @param {(option: SelectOption) => string} [getOptionsLabel] - A callback function to get the label from option object, defaults to [option.label || option[optionsIdProperty]].
 * @default undefined
 * @param {(option: SelectOption) => string} [getDisplayLabel] - A callback function to get the display label from option object, defaults to [getOptionsLabel].
 * @default undefined
 * @param {boolean} [disabled] - A flag to disable the dropdown.
 * @default false
 * @param {boolean} [isSearchable] - A flag to add search input to the dropdown.
 * @default false
 * @param {boolean} [forceMenuWidth] - A flag to match the width of the dropdown to the select
 * @default true
 * @param {string} [placeholder] - An optional text that goes as a placeholder inside the dropdown.
 * @default ''
 * @param {string} [searchPlaceholder] - An optional text that goes as a placeholder for the search input inside the dropdown.
 * @default undefined
 * @param {string} [className] - An optional class name to be added to the wrapper div.
 * @default ''
 * @param {string} [iconComponent] - An optional icon component to be used as the dropdown arrow.
 * @default undefined
 * @param {ReactNode} [icon] - An optional icon to be displayed in the dropdown.
 * @default undefined
 * @param {string} ['data-testid'] - An optional id to set to the select.
 * @default 'select'
 * @param {(option: SelectOption | SelectOption[]) => void} [onChange] - A callback function to be called when an option is selected or unselected.
 * @default undefined
 * @param {(isOpen: boolean) => void} [onMenuToggle] - A callback function to be called when the menu is opened or closed.
 * @default undefined
 */
export const Select = ({
  value = undefined,
  options = [],
  optionsIdProperty = 'id',
  getOptionsLabel,
  getDisplayLabel,
  disabled = false,
  isSearchable = false,
  forceMenuWidth = true,
  placeholder = '',
  searchPlaceholder,
  className = '',
  itemsClassName,
  iconComponent: Icon = ChevronIcon,
  icon,
  'data-testid': testId = 'select',
  onChange,
  onMenuToggle,
  ...props
}: SelectProps) => {
  getOptionsLabel = getOptionsLabel || ((option: SelectOption) => option?.label || option?.[optionsIdProperty]);
  getDisplayLabel = getDisplayLabel || getOptionsLabel;

  const selectRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_BREAKPOINT).matches : false
  );

  const isMultiselect = Array.isArray(value);

  const closeMenu = () => {
    setIsOpen(false);
    onMenuToggle?.(false);
  };

  const openMenu = () => {
    setIsOpen(true);
    onMenuToggle?.(true);
  };

  const toggleMenu = () => {
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const selectToggleMenu = (event: MouseEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) toggleMenu();
  };

  const selectOption = (option: SelectOption) => {
    onChange?.(option);
    closeMenu();
  };

  const deleteOption = (option: SelectOption) => {
    if (isMultiselect) {
      const id = option[optionsIdProperty];
      onChange?.([...value.filter((selectedValues) => selectedValues[optionsIdProperty] !== id)]);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') toggleMenu();
  };

  useEffect(() => {
    const media = window.matchMedia(MOBILE_BREAKPOINT);
    const onChange = () => setIsMobile(media.matches);

    media.addEventListener('change', onChange);

    return () => media.removeEventListener('change', onChange);
  }, []);

  const selectedLabel = !isMultiselect && value ? getDisplayLabel(value) : '';

  return (
    <div
      className={classNames(
        styles.select,
        className,
        isOpen && styles.isOpen,
        disabled && styles.disabled,
        (isMultiselect && value.length > 0) || (!isMultiselect && typeof value !== 'undefined' && styles.hasValue)
      )}
      ref={selectRef}
      {...props}
    >
      <div
        role="button"
        data-testid={testId}
        aria-disabled={disabled}
        tabIndex={0}
        className={styles.button}
        onClick={selectToggleMenu}
        onKeyDown={handleKeyDown}
      >
        {icon}

        {isMultiselect && value.length > 0 && (
          <div className={styles.chips}>
            {value.map((option) => (
              <InputChip
                key={option[optionsIdProperty]}
                data-testid={`${testId}-chip-${option[optionsIdProperty]}`}
                value={getOptionsLabel(option)}
                disabled={option?.disabled || disabled}
                aria-disabled={option?.disabled || disabled}
                onDelete={() => deleteOption(option)}
              />
            ))}
          </div>
        )}

        {(!isMultiselect || (isMultiselect && value.length === 0)) && (
          <div className={styles.buttonText}>{selectedLabel || (disabled ? '' : placeholder)}</div>
        )}

        <Icon className={styles.arrow} />
      </div>

      <SelectMenu
        testId={testId}
        className={itemsClassName}
        isOpen={isOpen}
        onClose={closeMenu}
        value={value}
        options={options}
        optionsIdProperty={optionsIdProperty}
        getOptionsLabel={getOptionsLabel}
        isSearchable={isSearchable}
        isMultiselect={isMultiselect}
        forceMenuWidth={forceMenuWidth}
        searchPlaceholder={searchPlaceholder}
        onChange={onChange}
        ref={selectRef}
      />

      {isMobile && !isMultiselect && (
        <select
          data-testid={`${testId}-native`}
          className={classNames(styles.nativeSelect, className)}
          value={value ? value[optionsIdProperty] : undefined}
          disabled={disabled}
          onChange={(event) =>
            selectOption(
              options.find((option) => option[optionsIdProperty] === event.target.value) || ({} as SelectOption)
            )
          }
        >
          {options.map((option) => (
            <option key={option[optionsIdProperty]} value={option[optionsIdProperty]} disabled={option?.disabled}>
              {`${getOptionsLabel(option)}${option.hint ? ` - ${option.hint}` : ''}`}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};
